import {
  LegacyPortalPurchaseHistoryOrderStatus,
  LegacyPortalPurchaseHistoryProductType,
} from "@spwn-portal/grpc";
import { PAYMENT_TYPE } from "constants/paymentType";
import { useGetPurchaseHistoryOrder } from "features/purchaseHistory/useGetPurchaseHistoryOrder";
import { useI18n } from "hooks/i18n/i18n";
import { useMemo } from "react";
import { isPayPalPendingError } from "utility/paypal";

import { PAYMENT_STATUS } from "../../constants/PaymentStatus";
import { convertShippingAddressToDeliveryData } from "../../utils/convertOrderToDeliveryData";
import { convertOrderToOrderData } from "../../utils/convertOrderToOrderData";
import { convertOrderToOrderList } from "../../utils/convertOrderToOrderList";
import { resolveDeliveryShippingUniqueId } from "../../utils/resolveDeliveryShippingUniqueId";
import { toDeliveryData } from "../../utils/toDeliveryData";
import { useOrderAddress } from "../useOrderAddress";
import { useOrderFulfillment } from "../useOrderFulfillment";
import { useTicketBonusMap } from "../useTicketBonusMap";
import { useUnpaidPaymentModalFromApi } from "../useUnpaidPaymentModalFromApi";

import type { OrderDetailSource } from "../../types/orderDetailSource";
import type { TicketIdsByEvent } from "../useTicketBonusMap";
import type { LegacyPortalPurchaseHistoryOrder } from "@spwn-portal/grpc";

type Input = {
  /** falseの間は取得を始めない。取得元の切り替え中に二重取得しないため */
  enabled: boolean;
  orderId: string;
};

/**
 * 注文に含まれるチケットをイベントごとにまとめる。特典メディアの有無をイベント単位で問い合わせるため。
 * チケットIDが取れない明細（商品参照が欠けた古い注文）は問い合わせに含めない
 */
const groupTicketIdsByEvent = (
  order: LegacyPortalPurchaseHistoryOrder
): TicketIdsByEvent[] => {
  const ticketIdsMap = new Map<string, string[]>();
  order.items.forEach((item) => {
    if (item.productType !== LegacyPortalPurchaseHistoryProductType.TICKET)
      return;
    if (item.eid === undefined || item.detail.case !== "spwn") return;
    const ticketId = item.detail.value.productFirestoreId;
    if (ticketId === "") return;
    ticketIdsMap.set(item.eid, [
      ...(ticketIdsMap.get(item.eid) ?? []),
      ticketId,
    ]);
  });
  return Array.from(ticketIdsMap.entries()).map(([eventId, ticketIds]) => ({
    eventId,
    ticketIds,
  }));
};

/** 購入履歴APIから取得する取得元。取得元がFirestoreかRDBかは注文の値に含まれ、ここでは区別しない */
export const useOrderDetailFromApi = (input: Input): OrderDetailSource => {
  const { enabled, orderId } = input;
  const { t } = useI18n();
  const { error, isNotFound, order } = useGetPurchaseHistoryOrder({
    enabled,
    orderId,
  });

  const {
    fulfillmentData,
    shippingUniqueId: fulfillmentShippingUniqueId,
    isLoading: isLoadingFulfillment,
    isError: isFulfillmentError,
  } = useOrderFulfillment({
    fulfillmentFirestoreId: order?.fulfillmentFirestoreIds[0] ?? null,
    orderId: order?.orderId ?? null,
  });
  // Shopify注文は決済時の住所スナップショットを持つ。SPWN注文は配送先IDから住所を引く
  const { addressData, isLoading: isLoadingAddressData } = useOrderAddress({
    shippingUniqueId: resolveDeliveryShippingUniqueId({
      orderShippingUniqueId: order?.shippingUniqueId ?? null,
      fulfillmentShippingUniqueId,
      isLoadingFulfillment,
      isFulfillmentError,
    }),
  });
  // 出庫の取得を待つ間も住所は読み込み中として扱う
  const isLoadingAddress = isLoadingFulfillment || isLoadingAddressData;
  const ticketsByEvent = useMemo(() => {
    if (!order) return null;
    return groupTicketIdsByEvent(order);
  }, [order]);
  const { bonusMap, isLoadingBonus } = useTicketBonusMap({
    orderId: order?.orderId ?? null,
    ticketsByEvent,
  });

  const orderData = useMemo(() => {
    if (!order) return null;
    return convertOrderToOrderData(order);
  }, [order]);
  const orderList = useMemo(() => {
    if (!order) return [];
    return convertOrderToOrderList({ bonusMap, order, t });
  }, [bonusMap, order, t]);

  const deliveryData = useMemo(() => {
    if (order?.shippingAddress !== undefined) {
      return convertShippingAddressToDeliveryData(order.shippingAddress);
    }
    if (!addressData) return null;
    return toDeliveryData(addressData);
  }, [addressData, order?.shippingAddress]);

  const isPurchased =
    orderData !== null && orderData.paymentStatus === PAYMENT_STATUS.PAYSUCCESS;

  // 注文状態のenumで判定する。OrderDataのpaymentStatusは未知の状態もUNPROCESSに寄せるため、
  // 状態が分からない注文にまで支払案内を出してしまう。
  // 支払期限切れ等でキャンセル済みの注文もUNPROCESSのまま返り、APIはその場合unpaidPaymentInfoを付けない。
  // 支払案内を出せない注文にボタンだけ出さないよう、unpaidPaymentInfoの有無も条件にする
  const isUnprocessed =
    order?.status === LegacyPortalPurchaseHistoryOrderStatus.UNPROCESS &&
    order.unpaidPaymentInfo !== undefined;

  const isPayPalPending =
    order?.payType === PAYMENT_TYPE.PayPal &&
    isUnprocessed &&
    isPayPalPendingError(order.unpaidPaymentInfo?.errorInfo ?? null);

  const showPaymentAlert = !isPurchased && isUnprocessed;

  const {
    openConfirmCvsPayment: handleContinuePayment,
    renderConfirmCvsPayment,
  } = useUnpaidPaymentModalFromApi({ order });

  return {
    deliveryData,
    fulfillmentData,
    handleContinuePayment,
    isFetchError: error !== undefined,
    isInvalid: isNotFound,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    isPayPalPending,
    isPurchased,
    orderData,
    orderList,
    renderConfirmCvsPayment,
    showPaymentAlert,
  };
};
